/**
 * Abonnement Fonbex — souscription Stripe + gestion in-app.
 *
 * V1 commerciale : plan mensuel unique.
 *   • 250 premiers clients au tarif de lancement (39 €/mois)
 *   • Standard 49 €/mois automatiquement ensuite
 *   • Bêta -50 % à vie appliquée automatiquement (coupon Stripe)
 *
 * Cette page permet aussi :
 *   • Résiliation directe (période courante conservée) via /api/payments/cancel
 *   • Réactivation avant fin de période via /api/payments/resume
 *   • Historique des factures via /api/payments/invoices
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle2, Sparkles, ShieldCheck, Loader2, Rocket, TrendingDown,
  Download, ExternalLink, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  "SCI, holdings, immeubles & lots illimités",
  "Génération PDF des 7 types de baux français",
  "États des lieux entrée/sortie avec photos",
  "OCR IA sur factures & documents (Claude + Gemini)",
  "Quittances automatiques + envoi e-mail",
  "Centre de conformité & Mon Assistant IA",
  "Crédits, patrimoine, comptabilité pré-préparée",
  "Multi-utilisateurs, rôles granulaires",
  "Exports PDF, Excel, CSV illimités",
  "Assistance par e-mail sous 24 h ouvrées",
];

function euroCents(cents) {
  if (cents == null) return "—";
  return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
}
function euroPlain(n) {
  if (n == null) return "—";
  return `${Number(n).toFixed(2).replace(".", ",")} €`;
}
function formatDate(ts) {
  if (!ts) return "—";
  const d = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleDateString("fr-FR");
}

export default function SubscriptionPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [offer, setOffer] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const isBeta = !!(user?.beta_program?.enrolled);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, o, s, inv] = await Promise.all([
        api.get("/payments/plans").then((r) => r.data).catch(() => null),
        api.get("/landing/offer-status").then((r) => r.data).catch(() => null),
        api.get("/payments/subscription").then((r) => r.data).catch(() => null),
        api.get("/payments/invoices").then((r) => r.data.items || []).catch(() => []),
      ]);
      setPlans(p); setOffer(o); setSubscription(s); setInvoices(inv);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const monthly = plans?.monthly;
  const discount = plans?.beta_discount_pct || 50;
  const finalPriceCents = useMemo(() => {
    if (!monthly) return null;
    if (!isBeta) return monthly.unit_amount;
    return Math.round(monthly.unit_amount * (1 - discount / 100));
  }, [monthly, isBeta, discount]);

  const currentTierBadge = useMemo(() => {
    if (isBeta) return { label: "Bêta -50 % à vie", tone: "accent" };
    if (offer?.launch && !offer.launch.is_locked) return { label: "Lancement — tarif bloqué", tone: "primary" };
    return { label: "Tarif standard", tone: "muted" };
  }, [isBeta, offer]);

  const startCheckout = async () => {
    if (!monthly) return;
    setStarting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: monthly.lookup_key,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else { toast.error("Réponse Stripe inattendue"); setStarting(false); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'ouvrir Stripe");
      setStarting(false);
    }
  };

  const cancelSub = async () => {
    setCancelling(true);
    try {
      await api.post("/payments/cancel");
      toast.success("Résiliation programmée en fin de période");
      setCancelDialogOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Résiliation impossible");
    } finally { setCancelling(false); }
  };

  const resumeSub = async () => {
    setCancelling(true);
    try {
      await api.post("/payments/resume");
      toast.success("Abonnement réactivé");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Réactivation impossible");
    } finally { setCancelling(false); }
  };

  const openStripePortal = async () => {
    try {
      const { data } = await api.post("/payments/portal",
        { origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portail Stripe indisponible");
    }
  };

  return (
    <div className="animate-fade-up max-w-5xl">
      <PageHeader
        overline="Abonnement"
        title="Formule Fonbex Pro"
        description="Un seul plan mensuel tout-inclus. Résiliation à tout moment en 1 clic. Vous conservez l'accès jusqu'à la fin de la période payée."
      />

      {/* ─── État courant de l'abonnement ─── */}
      {loading ? (
        <div className="text-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
          Chargement de votre abonnement…
        </div>
      ) : subscription?.has_subscription ? (
        <ActiveSubscriptionCard
          sub={subscription}
          onCancel={() => setCancelDialogOpen(true)}
          onResume={resumeSub}
          onPortal={openStripePortal}
          busy={cancelling}
        />
      ) : (
        <SubscribeCard
          offer={offer}
          monthly={monthly}
          isBeta={isBeta}
          discount={discount}
          finalPriceCents={finalPriceCents}
          currentTierBadge={currentTierBadge}
          onCheckout={startCheckout}
          starting={starting}
        />
      )}

      {/* ─── Historique des factures ─── */}
      {subscription?.has_subscription && (
        <div className="mt-8">
          <h3 className="font-heading text-lg mb-3">Historique des factures</h3>
          {invoices.length === 0 ? (
            <div className="rounded-2xl border border-border bg-white p-6 text-center text-sm text-muted-foreground">
              Aucune facture pour l&apos;instant. La première apparaîtra dès le premier prélèvement.
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-white overflow-x-auto"
                 data-testid="subscription-invoices-table">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Montant</th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoices.map((i) => (
                    <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                      <td className="px-4 py-3">{formatDate(i.created_at)}</td>
                      <td className="px-4 py-3 font-mono">{euroCents(i.amount_paid || i.amount_due)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge tone={
                          i.status === "paid" ? "success"
                          : i.status === "open" ? "warning"
                          : "neutral"
                        }>
                          {i.status}
                        </StatusBadge>
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        {i.invoice_pdf && (
                          <a href={i.invoice_pdf} target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                             data-testid={`invoice-pdf-${i.id}`}>
                            <Download className="h-3 w-3" /> PDF
                          </a>
                        )}
                        {i.hosted_invoice_url && (
                          <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                             data-testid={`invoice-view-${i.id}`}>
                            <ExternalLink className="h-3 w-3" /> Voir
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── Dialog de confirmation de résiliation ─── */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Résilier votre abonnement ?
            </DialogTitle>
            <DialogDescription>
              Vous conserverez l&apos;accès à toutes les fonctionnalités jusqu&apos;au{" "}
              <strong>{formatDate(subscription?.current_period_end)}</strong>.
              Aucun prélèvement supplémentaire ne sera effectué. Vous pouvez
              réactiver l&apos;abonnement à tout moment avant cette date.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}
                    data-testid="cancel-dialog-abort">
              Annuler
            </Button>
            <Button variant="destructive" onClick={cancelSub} disabled={cancelling}
                    data-testid="cancel-dialog-confirm">
              {cancelling ? "Résiliation…" : "Confirmer la résiliation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="mt-8 text-center text-xs text-muted-foreground">
        Un doute&nbsp;? Écrivez-nous à
        {" "}<a href="mailto:contact@fonbex.fr" className="underline">contact@fonbex.fr</a>{" "}
        — nous répondons sous 24 h ouvrées.
      </p>
    </div>
  );
}

/* ------------------ Souscription ------------------ */
function SubscribeCard({ offer, monthly, isBeta, discount, finalPriceCents, currentTierBadge, onCheckout, starting }) {
  if (!monthly) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        Aucune formule disponible pour le moment. Contactez-nous.
      </div>
    );
  }
  return (
    <>
      {isBeta && (
        <div data-testid="subscription-beta-banner"
             className="mb-6 rounded-2xl border border-accent/30 bg-accent/5 p-4 flex items-start gap-3">
          <Sparkles className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold">Vous êtes bêta-testeur Fonbex</div>
            <p className="text-muted-foreground mt-1">
              Votre remise permanente de <strong>-{discount} %</strong> sera appliquée
              automatiquement à chaque échéance — pas de code à saisir.
            </p>
          </div>
        </div>
      )}

      {!isBeta && offer?.launch && !offer.launch.is_locked && (
        <div data-testid="subscription-launch-banner"
             className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
          <Rocket className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold">Offre lancement active — {offer.launch.remaining_slots} places restantes sur 250</div>
            <p className="text-muted-foreground mt-1">
              En souscrivant maintenant, vous bloquez le tarif de <strong>{euroPlain(offer.launch.current_price_eur)}</strong>/mois
              à vie (économie de <strong>{euroPlain(offer.launch.regular_price_eur - offer.launch.current_price_eur)}</strong> par mois vs {euroPlain(offer.launch.regular_price_eur)} standard).
            </p>
          </div>
        </div>
      )}

      <div className="card-elevated p-6 md:p-10" data-testid="subscription-card">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-overline text-accent">Fonbex Pro</div>
              <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                currentTierBadge.tone === "accent" ? "bg-accent/10 text-accent"
                : currentTierBadge.tone === "primary" ? "bg-primary/10 text-primary"
                : "bg-muted text-muted-foreground"
              }`}>
                {currentTierBadge.label}
              </span>
            </div>
            <h2 className="mt-2 font-serif text-3xl">Tout Fonbex, sans limite</h2>
            <p className="mt-3 text-muted-foreground">
              Idéal pour les propriétaires actifs, SCI et holdings qui veulent
              automatiser la gestion complète de leur patrimoine immobilier.
            </p>
            <ul className="mt-6 space-y-2">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="w-full md:w-80 flex-shrink-0 md:sticky md:top-24">
            <div className="rounded-2xl border border-border bg-secondary/20 p-6 text-center">
              {isBeta ? (
                <>
                  <div className="text-sm text-muted-foreground line-through">
                    {euroCents(monthly.unit_amount)}
                  </div>
                  <div className="mt-1 font-serif text-4xl text-primary" data-testid="subscription-price">
                    {euroCents(finalPriceCents)}
                  </div>
                  <div className="text-xs text-accent font-bold mt-1">-{discount} % bêta à vie</div>
                  <div className="mt-2 flex items-center justify-center gap-1 text-[11px] text-emerald-700 font-semibold">
                    <TrendingDown className="h-3 w-3" />
                    Économie {euroCents(monthly.unit_amount - finalPriceCents)} / mois
                  </div>
                </>
              ) : (
                <div className="font-serif text-4xl text-primary" data-testid="subscription-price">
                  {euroCents(monthly.unit_amount)}
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-2">
                facturé chaque mois, résiliable à tout moment
              </div>
              <Button onClick={onCheckout} disabled={starting}
                      className="btn-pill mt-6 w-full"
                      data-testid="subscription-checkout-btn">
                {starting ? "Redirection…" : <>Passer à Pro</>}
              </Button>
              <div className="mt-4 flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground uppercase tracking-widest">
                <ShieldCheck className="h-3 w-3" />
                Paiement sécurisé via Stripe
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------ État abonné (gestion cancel/resume) ------------------ */
function ActiveSubscriptionCard({ sub, onCancel, onResume, onPortal, busy }) {
  const statusTone = {
    active: "success", trialing: "info",
    past_due: "warning", canceled: "neutral", unpaid: "danger",
  }[sub.status] || "neutral";
  const price = sub.price || {};
  const willCancel = sub.cancel_at_period_end;

  return (
    <div className="card-elevated p-6 md:p-8 space-y-6" data-testid="active-subscription-card">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="text-overline text-accent">Votre abonnement</div>
          <h2 className="mt-2 font-serif text-2xl">Fonbex Pro</h2>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <StatusBadge tone={statusTone}>{sub.status}</StatusBadge>
            {willCancel && (
              <StatusBadge tone="warning" data-testid="sub-will-cancel-badge">
                Résiliation programmée
              </StatusBadge>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-serif" data-testid="sub-current-price">
            {euroCents(price.amount)}
          </div>
          <div className="text-xs text-muted-foreground">
            / {price.interval === "year" ? "an" : "mois"}
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-secondary/30 p-4 text-sm space-y-2">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Prochaine échéance</span>
          <span className="font-medium">{formatDate(sub.current_period_end)}</span>
        </div>
        {willCancel && (
          <div className="text-xs text-amber-700 pt-1">
            Vous conservez l&apos;accès Premium jusqu&apos;au{" "}
            <strong>{formatDate(sub.current_period_end)}</strong>. Après cette date,
            l&apos;abonnement ne sera pas renouvelé. Vous pouvez le réactiver à
            tout moment avant.
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        {willCancel ? (
          <Button onClick={onResume} disabled={busy}
                  className="btn-pill"
                  data-testid="sub-resume-btn">
            Réactiver mon abonnement
          </Button>
        ) : (
          <Button variant="outline" onClick={onCancel} disabled={busy}
                  data-testid="sub-cancel-btn">
            Résilier
          </Button>
        )}
        <Button variant="ghost" onClick={onPortal} data-testid="sub-portal-btn">
          <ExternalLink className="h-4 w-4 mr-1.5" />
          Ouvrir le portail Stripe (moyen de paiement, adresse…)
        </Button>
      </div>
    </div>
  );
}
